import type {
  ActiveFormDescriptorField,
  CoerceRejectReason,
  IdentityCandidate,
} from './types';

/**
 * Display types whose values are IDENTITIES — a person, a group, a tag type, a
 * department type — resolved from a name rather than matched against a published
 * option set (AID-394).
 *
 * The difference from `CLOSED_SET_DISPLAY_TYPES` is not one of degree. A closed
 * set works because the descriptor publishes every legal value, so the published
 * set IS the allowlist and a hallucinated value cannot reach `setValue`. For
 * `Owners` the legal set is every user in the org: unbounded, and not something
 * to ship in a prompt on every turn. So nothing is published up, the model
 * proposes a NAME, and the guard moves from publish time to resolve time —
 * resolve, and reject anything that does not resolve to exactly one match.
 */
export const IDENTITY_DISPLAY_TYPES = ['users', 'tags', 'departments'] as const;

export type IdentityDisplayType = (typeof IDENTITY_DISPLAY_TYPES)[number];

export const isIdentityDisplayType = (
  displayType: string
): displayType is IdentityDisplayType =>
  (IDENTITY_DISPLAY_TYPES as readonly string[]).includes(displayType);

/** Why one proposed name did not resolve to exactly one identity. */
export type IdentityMatchFailure = 'identity_ambiguous' | 'identity_no_match';

export type IdentityMatch =
  | { ok: false; reason: IdentityMatchFailure }
  | { ok: true; candidate: IdentityCandidate };

/**
 * Resolve one proposed name against a field's identity vocabulary — the SINGLE
 * rule for "which person, group, tag or department did the agent mean?".
 *
 * Shared deliberately, for the same reason `matchDescriptorOption` is: this
 * decides what Apply writes AND what `formatProposalValue` renders on the card.
 * If the two drifted, the card could show a colleague's name that Apply then
 * rejects — or worse, one name while writing another's id.
 *
 * Matching is EXACT on the label, case-insensitively and trimmed. Deliberately
 * NOT fuzzy: a near-miss on a person's name must fail, because "Jon Smith" and
 * "John Smith" are different people and there is no edit distance at which
 * guessing between them is acceptable.
 *
 * Case folding is `toLowerCase`, NOT `toLocaleLowerCase` — the one place this
 * deliberately differs from `matchDescriptorOption`. The host locale changes how
 * some letters fold (Turkish `I`/`İ` most notably, which occurs in real names),
 * so a locale-sensitive fold would make the same proposal resolve on one
 * colleague's machine and be rejected on another's. Neither outcome is a
 * wrong-person risk — both sides of the comparison fold identically, so a
 * divergence can only merge two labels into an ambiguity rejection or split one
 * into a miss — but it would make the rule's behaviour depend on who is looking
 * at it, which is not a property a resolution rule should have.
 *
 * Ids are deliberately NOT matched, even though a proposed id present in this
 * list could be resolved safely. The published contract is names (see the
 * system-prompt guidance), so nothing legitimately proposes an id, and a second
 * matching rule with no caller is just a wider surface to reason about.
 *
 * Zero matches and several matches are reported SEPARATELY rather than as one
 * "unresolved": the user's next action differs, so the card's copy has to.
 */
export const matchIdentityCandidate = (
  candidates: IdentityCandidate[],
  raw: unknown
): IdentityMatch => {
  // Names are strings. A number or object proposed for a person field is not a
  // near-miss to be salvaged.
  if (typeof raw !== 'string') {
    return { ok: false, reason: 'identity_no_match' };
  }

  const folded = raw.trim().toLowerCase();
  if (folded === '') {
    return { ok: false, reason: 'identity_no_match' };
  }

  const matches = candidates.filter(
    (candidate) => candidate.label.trim().toLowerCase() === folded
  );

  if (matches.length === 0) {
    return { ok: false, reason: 'identity_no_match' };
  }
  if (matches.length > 1) {
    return { ok: false, reason: 'identity_ambiguous' };
  }

  return { ok: true, candidate: matches[0] };
};

/**
 * The candidates a given field can legally hold.
 *
 * A single-valued `users` field takes USERS ONLY, and that is a correctness
 * requirement rather than a preference. Every `multiple: false` user field in
 * the app (`Assignee`, `Submitter`, `ReviewedBy`, `AssignedUser`,
 * `CompletedByUser`) is backed by `UserOptionSchema`, whose `type` is
 * `z.enum(['user'])` — a group is not representable there, so writing one would
 * fail the field's own zod validation on `shouldValidate: true`. Their pickers
 * pass `includeGroups={false}` for the same reason, so the user is never offered
 * a group either.
 *
 * Filtering by legality BEFORE matching also sharpens the ambiguity rule. On a
 * multi-valued field a name belonging to both a person and a group is genuinely
 * ambiguous and is rejected. On a single-valued field the group was never a
 * candidate, so the same name resolves to the person — not a silent choice
 * between two legal values, but the only legal one.
 */
export const assignableIdentityCandidates = (
  field: ActiveFormDescriptorField,
  candidates: IdentityCandidate[]
): IdentityCandidate[] =>
  field.displayType === 'users' && field.multiple !== true
    ? candidates.filter((candidate) => candidate.kind !== 'userGroup')
    : candidates;

/**
 * Wrap a resolved identity in the shape the field's zod schema expects.
 *
 * Three shapes, one per identity type — `UserOrGroupSchema`'s
 * `{ type, value }`, and the `{ TagTypeId }` / `{ DepartmentTypeId }` wrappers.
 * `kind` defaults to `'user'` so a tag or department candidate (which carries
 * none) can never produce a half-built user value.
 */
const toIdentityFieldValue = (
  displayType: IdentityDisplayType,
  candidate: IdentityCandidate
): unknown => {
  if (displayType === 'tags') {
    return { TagTypeId: candidate.id };
  }
  if (displayType === 'departments') {
    return { DepartmentTypeId: candidate.id };
  }

  return { type: candidate.kind ?? 'user', value: candidate.id };
};

/**
 * Coerce agent-proposed NAMES into the UUID-bearing value an identity field
 * holds (AID-394).
 *
 * Multi-valued fields (`tags`, `departments`, and `users` with
 * `multiple: true`) write an array; a single-valued `users` field writes one
 * bare object, matching `UserOptionSchema` rather than wrapping it.
 *
 * All or nothing, like the closed-set path: one unresolvable name rejects the
 * whole field rather than writing a partial selection the user is about to
 * review. A selection missing the person who matters is not a smaller version of
 * the right answer.
 */
export const resolveIdentityValue = (
  field: ActiveFormDescriptorField,
  raw: unknown
): { ok: false; reason: CoerceRejectReason } | { ok: true; value: unknown } => {
  if (!isIdentityDisplayType(field.displayType)) {
    return { ok: false, reason: 'unsupported_type' };
  }

  const candidates = field.identityCandidates;
  // Fail-closed, exactly as a closed-set field with no resolved options is:
  // a vocabulary that has not loaded is "no name can be resolved", not "any
  // name goes". `buildActiveFormDescriptor` never publishes one, so this is the
  // independent guard rather than the expected path.
  if (!candidates || candidates.length === 0) {
    return { ok: false, reason: 'unsupported_type' };
  }

  const assignable = assignableIdentityCandidates(field, candidates);
  // `tags` and `departments` are always arrays of wrappers; only `users` varies,
  // on the form config's own `multiple`.
  const isMultiValued =
    field.displayType !== 'users' || field.multiple === true;

  // The proposal tool carries `value` as a string OR an array of strings, and
  // one name is the common case even for a multi-valued field.
  const proposed = Array.isArray(raw) ? raw : [raw];
  if (proposed.length === 0) {
    return { ok: false, reason: 'invalid_value' };
  }
  // A single-valued field given SEVERAL names is rejected rather than truncated
  // to the first — there is no non-arbitrary way to pick which colleague was
  // meant. A one-element array is still accepted: it is unambiguous, and the
  // tool's value type invites a model to wrap. (The closed-set path rejects any
  // array here; the values there are interchangeable enough that the stricter
  // rule costs nothing, whereas dropping a resolvable sole assignee does.)
  if (!isMultiValued && proposed.length > 1) {
    return { ok: false, reason: 'invalid_value' };
  }

  const values: unknown[] = [];
  const seen = new Set<string>();
  for (const name of proposed) {
    const match = matchIdentityCandidate(assignable, name);
    if (!match.ok) {
      return { ok: false, reason: match.reason };
    }
    // Two spellings of one colleague are one assignee, not two identical rows.
    if (seen.has(match.candidate.id)) {
      continue;
    }
    seen.add(match.candidate.id);
    values.push(toIdentityFieldValue(field.displayType, match.candidate));
  }

  return { ok: true, value: isMultiValued ? values : values[0] };
};
